// État de session : connexion en deux étapes, contexte actif, changement de collectivité
// (qui vide tous les caches de requêtes), déconnexion et expiration.
import type { EtapeMfa, SessionOuverte } from '@cc/contrats';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { definirJeton, ecouterSession, jetonPresent, rafraichir, requete, requetePublique } from '../api/client';
import type { ContexteActif } from '../api/types';

export type EtatSession = 'initialisation' | 'deconnecte' | 'connecte';

interface ValeurSession {
  etat: EtatSession;
  contexte: ContexteActif | null;
  chargementContexte: boolean;
  erreurContexte: unknown;
  /** Vrai quand la session a expiré pendant l'utilisation (message à la connexion). */
  expiree: boolean;
  connecter: (identifiant: string, motDePasse: string) => Promise<'connecte' | EtapeMfa>;
  verifierMfa: (jetonTemporaire: string, code: { code?: string; code_secours?: string }) => Promise<void>;
  activerContexte: (appartenanceId: string) => Promise<void>;
  recharger: () => Promise<unknown>;
  deconnecter: () => Promise<void>;
  /** Rouvre la session par le cookie de rafraîchissement (zone terrain, au retour du réseau) ; false si elle est close. */
  reprendre: () => Promise<boolean>;
}

const ContexteSession = createContext<ValeurSession | null>(null);

export const CLE_CONTEXTE = ['contexte'] as const;

export function FournisseurSession({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const [etat, setEtat] = useState<EtatSession>(() => (jetonPresent() ? 'connecte' : 'initialisation'));
  const [expiree, setExpiree] = useState(false);

  // Au chargement : reprise de la session existante par le cookie de rafraîchissement.
  useEffect(() => {
    if (etat !== 'initialisation') return;
    let actif = true;
    void rafraichir().then((ok) => {
      if (actif) setEtat(ok ? 'connecte' : 'deconnecte');
    });
    return () => {
      actif = false;
    };
  }, [etat]);

  useEffect(
    () =>
      ecouterSession((evenement) => {
        if (evenement === 'session_expiree') {
          setExpiree(true);
          setEtat('deconnecte');
          client.clear();
        }
      }),
    [client],
  );

  const requeteContexte = useQuery({
    queryKey: CLE_CONTEXTE,
    queryFn: ({ signal }) => requete<ContexteActif>('/contexte', { signal }),
    enabled: etat === 'connecte',
    staleTime: 60_000,
  });

  const ouvrir = useCallback(
    (s: SessionOuverte) => {
      client.clear();
      definirJeton(s.jeton_acces, s.expire_dans);
      setExpiree(false);
      setEtat('connecte');
    },
    [client],
  );

  const connecter = useCallback(
    async (identifiant: string, motDePasse: string) => {
      const r = await requetePublique<SessionOuverte | EtapeMfa>('/auth/connexion', { identifiant, mot_de_passe: motDePasse });
      if (r.etape === 'mfa') return r;
      ouvrir(r);
      return 'connecte' as const;
    },
    [ouvrir],
  );

  const verifierMfa = useCallback(
    async (jetonTemporaire: string, code: { code?: string; code_secours?: string }) => {
      const r = await requetePublique<SessionOuverte>('/auth/mfa', { jeton_temporaire: jetonTemporaire, ...code });
      ouvrir(r);
    },
    [ouvrir],
  );

  const activerContexte = useCallback(
    async (appartenanceId: string) => {
      const r = await requete<{ jeton_acces: string }>('/contexte/activer', { methode: 'POST', corps: { appartenance_id: appartenanceId } });
      definirJeton(r.jeton_acces, 15 * 60);
      const nouveau = await requete<ContexteActif>('/contexte');
      // Aucune donnée de l'ancien contexte ne doit survivre au changement (R14) : toutes les
      // requêtes sont retirées du cache, puis le contexte est remplacé en place (ce qui notifie
      // les composants abonnés).
      await client.cancelQueries();
      client.removeQueries({ predicate: (q) => q.queryKey[0] !== CLE_CONTEXTE[0] });
      client.setQueryData(CLE_CONTEXTE, nouveau);
    },
    [client],
  );

  const deconnecter = useCallback(async () => {
    try {
      await requete('/auth/deconnexion', { methode: 'POST' });
    } catch {
      // Session déjà close côté serveur : la déconnexion locale suffit.
    }
    definirJeton(null);
    client.clear();
    setExpiree(false);
    setEtat('deconnecte');
  }, [client]);

  const reprendre = useCallback(async () => {
    const ok = await rafraichir();
    if (ok) {
      setExpiree(false);
      setEtat('connecte');
    }
    return ok;
  }, []);

  const valeur = useMemo<ValeurSession>(
    () => ({
      etat,
      contexte: requeteContexte.data ?? null,
      chargementContexte: requeteContexte.isPending && etat === 'connecte',
      erreurContexte: requeteContexte.error,
      expiree,
      connecter,
      verifierMfa,
      activerContexte,
      recharger: () => requeteContexte.refetch(),
      deconnecter,
      reprendre,
    }),
    [etat, requeteContexte, expiree, connecter, verifierMfa, activerContexte, deconnecter, reprendre],
  );

  return <ContexteSession.Provider value={valeur}>{children}</ContexteSession.Provider>;
}

export function useSession(): ValeurSession {
  const v = useContext(ContexteSession);
  if (!v) throw new Error('useSession hors de FournisseurSession');
  return v;
}
